import { ReactElement, useMemo, useCallback, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Stack,
  TextField,
  Alert,
  Link,
  FormGroup,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useFormikContext } from 'formik';
import moment from 'moment';

import { DataPaginateResponse } from '~globals/types/api';
import { Nullable } from '~globals/types/commons';
import { searchVehicles } from '~services/vehicle';
import { VehicleSearchItem } from '~services/vehicle/types';
import {
  useSubscription,
  useUtcOffset,
  useToggle,
  useDataTableRequest,
} from '~hooks/index';
import {
  Subscription,
  PointAddressSelector,
  SelectWithScopeField,
} from '~components/index';
import { PointAddressSelectorOnChangeParams } from '~components/PointAddressSelector/types';
import { routeCalculationModesOptions } from '~constants/routeCalculationModes';
import { PATHS } from '~constants/paths';
import { hasError } from '~utils/formHelpers';
import { getStringifyUrlQueryString } from '~utils/router';
import { isPlanStart } from '~utils/plans';

import { AssistantGenerateRoutesDialogCapabilitiesInfo } from '../../../../styles';
import {
  AssistantGenerateRoutesDialogCollapsableCard,
  AssistantGenerateRoutesDialogScheduleRouteCardForm,
} from '../../../../Components';
import {
  getSelectedEntityDescription,
  getDefaultVehiclesRequestPayload,
  getDefaultScheduleStartDate,
  getLabelsPointAddressSelector,
} from '../../../../utils/helpers';
import { SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME } from '../../../../utils/scheduleRouteForm';

import { useAssistantGenerateRoutesDialogByItemsContext } from '../../../Context';
import {
  FORM_BY_ITEMS_FIELDS_NAME,
  getFormByItemsInitialValues,
} from '../../../utils';

import AssistantGenerateRoutesDialogByItemsContentFormItemTable from './ItemsTable';
import AssistantGenerateRoutesDialogByItemsContentFormVehiclesTable from './VehiclesTable';

const AssistantGenerateRoutesDialogByItemsContentForm = (): ReactElement => {
  const subscription = useSubscription();
  const formatterDateUtcOffset = useUtcOffset();

  const {
    onClose,
    data: {
      warehouses: { list: warehouses },
      vehicles: { setValue: setVehicles },
      selectedDriver: { value: selectedDriver, setValue: setSelectedDriver },
      isFeedbackRequestAudit,
    },
    form: {
      selection: {
        orders: { selectedRowsIds: selectedItemsIds },
        vehicles: {
          selectedRowsIds: selectedVehiclesIds,
          restarSelectedRows: restarVehiclesSelection,
        },
      },
      chargeIndicators: {
        orders: indicatorsItemssCharge,
        vehicles: indicatorsVehiclesCharge,
      },
      isScheduleRouteEnabled: { setValue: setContextIsScheduleRouteEnabled },
    },
  } = useAssistantGenerateRoutesDialogByItemsContext();

  const {
    touched,
    errors,
    values,
    getFieldProps,
    setFieldValue,
    setFieldTouched,
    validateForm,
  } = useFormikContext<ReturnType<typeof getFormByItemsInitialValues>>();

  const isScheduleRouteEnabled = useMemo(
    () => values[SCHEDULE_ROUTE_BASE_FORM_FIELDS_NAME.SCHEDULE_ROUTE_ENABLED],
    [values],
  );

  const handleEnabledScheduleRoute = useCallback(
    (isEnabled: boolean) => {
      restarVehiclesSelection();
      setContextIsScheduleRouteEnabled(isEnabled);
    },
    [restarVehiclesSelection, setContextIsScheduleRouteEnabled],
  );

  const currentScheduleDateValue = useMemo(
    () => values[FORM_BY_ITEMS_FIELDS_NAME.SCHEDULE_START_DATE],
    [values],
  );

  const [lastScheduleDateValue, setLastScheduleDateValuel] = useState(
    currentScheduleDateValue,
  );

  const handleChangeScheduleDate = useCallback(
    (value: Date | null): void => {
      setFieldValue(FORM_BY_ITEMS_FIELDS_NAME.SCHEDULE_START_DATE, value);
    },
    [setFieldValue],
  );

  const vehiclesTable = useDataTableRequest({
    request: searchVehicles,
    payload: getDefaultVehiclesRequestPayload(
      formatterDateUtcOffset(
        getDefaultScheduleStartDate(),
        moment.HTML5_FMT.DATETIME_LOCAL_MS,
        true,
      ),
    ),
    transformResponse: (response) => {
      const {
        data: { results },
      } = response.data as DataPaginateResponse<VehicleSearchItem[]>;

      setVehicles(results);

      return response.data;
    },
  });

  const handleChangeVehicleTableByScheduleDate = useCallback(
    (currentDate: Date | null) => {
      const isValidCurrentScheduleDate = moment(currentDate).isValid();

      const isEqualScheduleDate = moment(lastScheduleDateValue).isSame(
        currentDate,
      );

      if (isValidCurrentScheduleDate && !isEqualScheduleDate) {
        restarVehiclesSelection();

        vehiclesTable.handleChangeTable(
          'filter',
          getDefaultVehiclesRequestPayload(
            formatterDateUtcOffset(
              currentDate,
              moment.HTML5_FMT.DATETIME_LOCAL_MS,
              true,
            ),
          ),
        );
      }

      setLastScheduleDateValuel(currentDate);
    },
    [
      formatterDateUtcOffset,
      lastScheduleDateValue,
      restarVehiclesSelection,
      vehiclesTable,
    ],
  );

  const handleAcceptScheduleDate = useCallback(
    (value: Date | null) => handleChangeVehicleTableByScheduleDate(value),
    [handleChangeVehicleTableByScheduleDate],
  );

  const handleBlurScheduleDate = useCallback((): void => {
    setFieldTouched(FORM_BY_ITEMS_FIELDS_NAME.SCHEDULE_START_DATE, true);

    handleChangeVehicleTableByScheduleDate(currentScheduleDateValue);
  }, [
    setFieldTouched,
    handleChangeVehicleTableByScheduleDate,
    currentScheduleDateValue,
  ]);

  const handleChangePoint = useCallback(
    async (
      pointType: 'starting' | 'arrival',
      {
        data: pointSelectorData,
        type: pointSelectorType,
      }: PointAddressSelectorOnChangeParams,
    ) => {
      const POINT_KEYS: Record<string, string> = {
        TYPE: FORM_BY_ITEMS_FIELDS_NAME.STARTING_POINT_TYPE,
        ID: FORM_BY_ITEMS_FIELDS_NAME.STARTING_POINT_ID,
        ADDRESS: FORM_BY_ITEMS_FIELDS_NAME.STARTING_POINT_ADDRESS,
        ADDRESS_COORDS: FORM_BY_ITEMS_FIELDS_NAME.STARTING_POINT_ADDRESS_COORDS,
      };

      if (pointType === 'arrival') {
        POINT_KEYS.TYPE = FORM_BY_ITEMS_FIELDS_NAME.ARRIVAL_POINT_TYPE;
        POINT_KEYS.ID = FORM_BY_ITEMS_FIELDS_NAME.ARRIVAL_POINT_ID;
        POINT_KEYS.ADDRESS = FORM_BY_ITEMS_FIELDS_NAME.ARRIVAL_POINT_ADDRESS;
        POINT_KEYS.ADDRESS_COORDS =
          FORM_BY_ITEMS_FIELDS_NAME.ARRIVAL_POINT_ADDRESS_COORDS;
      }

      let pointCoords: Nullable<google.maps.LatLngLiteral> = null;

      if (pointSelectorData?.latitude && pointSelectorData.longitude) {
        pointCoords = {
          lat: pointSelectorData.latitude,
          lng: pointSelectorData.longitude,
        };
      }

      await setFieldValue(POINT_KEYS.TYPE, pointSelectorType);
      await setFieldValue(POINT_KEYS.ID, pointSelectorData?.id ?? '');
      await setFieldValue(POINT_KEYS.ADDRESS, pointSelectorData?.address ?? '');
      await setFieldValue(POINT_KEYS.ADDRESS_COORDS, pointCoords);

      await validateForm();
    },
    [setFieldValue, validateForm],
  );

  const handleBlurPoint = useCallback(
    (fieldName) => {
      setFieldTouched(fieldName, true);
    },
    [setFieldTouched],
  );

  const [expandedItemsCard, toggleExpandedItemsCard] = useToggle(false);
  const [expandedVehiclesCard, toogleExpandedVehiclesCard] = useToggle(true);

  return (
    <Stack spacing={2} paddingBottom={2}>
      <Stack direction="row" spacing={2}>
        <TextField
          {...getFieldProps(FORM_BY_ITEMS_FIELDS_NAME.NAME)}
          label="Nombre de ruta"
          error={hasError(touched, errors, FORM_BY_ITEMS_FIELDS_NAME.NAME)}
          fullWidth
          autoComplete="off"
        />

        <Subscription.DataRetentionMin.DateTimePicker
          label="Fecha de agenda"
          value={currentScheduleDateValue}
          onChange={handleChangeScheduleDate}
          onAccept={handleAcceptScheduleDate}
          renderInput={(props) => (
            <TextField
              {...props}
              onBlur={handleBlurScheduleDate}
              error={hasError(
                touched,
                errors,
                FORM_BY_ITEMS_FIELDS_NAME.SCHEDULE_START_DATE,
              )}
              fullWidth
              sx={{ width: '50%' }}
            />
          )}
        />
      </Stack>

      <Stack direction="row" spacing={2}>
        <PointAddressSelector
          warehouses={warehouses}
          labels={getLabelsPointAddressSelector({
            type: 'starting',
            isOptional: false,
          })}
          defaultValues={{
            warehouse: values[FORM_BY_ITEMS_FIELDS_NAME.STARTING_POINT_ID],
          }}
          onChange={(pointSelectorData) =>
            handleChangePoint('starting', pointSelectorData)
          }
          onReset={(pointSelectorType) =>
            handleChangePoint('starting', {
              type: pointSelectorType,
              data: null,
            })
          }
          onBlur={() =>
            handleBlurPoint(FORM_BY_ITEMS_FIELDS_NAME.STARTING_POINT_ADDRESS)
          }
          error={hasError(
            touched,
            errors,
            FORM_BY_ITEMS_FIELDS_NAME.STARTING_POINT_ADDRESS,
          )}
        />

        <PointAddressSelector
          warehouses={warehouses}
          labels={getLabelsPointAddressSelector({
            type: 'arrival',
            isOptional: !isFeedbackRequestAudit,
          })}
          defaultValues={{
            warehouse: values[FORM_BY_ITEMS_FIELDS_NAME.ARRIVAL_POINT_ID],
          }}
          onChange={(pointSelectorData) =>
            handleChangePoint('arrival', pointSelectorData)
          }
          onReset={(pointSelectorType) =>
            handleChangePoint('arrival', {
              type: pointSelectorType,
              data: null,
            })
          }
          onBlur={() =>
            handleBlurPoint(FORM_BY_ITEMS_FIELDS_NAME.ARRIVAL_POINT_ADDRESS)
          }
          error={hasError(
            touched,
            errors,
            FORM_BY_ITEMS_FIELDS_NAME.ARRIVAL_POINT_ADDRESS,
          )}
          enabledEmptyOptionOnWarehouses
        />

        <SelectWithScopeField
          {...getFieldProps(FORM_BY_ITEMS_FIELDS_NAME.CALCULATION_MODE)}
          label="Modo de Generación"
          options={routeCalculationModesOptions}
          error={hasError(
            touched,
            errors,
            FORM_BY_ITEMS_FIELDS_NAME.CALCULATION_MODE,
          )}
          fullWidth
          autoComplete="off"
        />
      </Stack>

      <Stack>
        <AssistantGenerateRoutesDialogScheduleRouteCardForm
          onEnabled={handleEnabledScheduleRoute}
          startDate={currentScheduleDateValue}
          infoText="Se generará automaticamente una plantilla para poder programar la ruta."
          withPreAssignedDriver
          currentPreAssignedDriver={selectedDriver}
          onAssigned={(currentDriver) => setSelectedDriver(currentDriver)}
        />
      </Stack>

      <Stack>
        <AssistantGenerateRoutesDialogCollapsableCard
          expanded={expandedItemsCard}
          onExpanded={() => toggleExpandedItemsCard()}
          title="PEDIDOS A PROGRAMAR"
          description={getSelectedEntityDescription(selectedItemsIds, 'pedido')}
          indicator={
            <AssistantGenerateRoutesDialogCapabilitiesInfo
              capacity1={indicatorsItemssCharge.capacity1}
              capacity2={indicatorsItemssCharge.capacity2}
              numberOfPackages={indicatorsItemssCharge.numberOfPackages}
              maximumVisits={null}
              fontSize={14}
              fontWeight="bold"
              autoFlow="row"
              rows={1}
              columns={3}
            />
          }
        >
          <AssistantGenerateRoutesDialogByItemsContentFormItemTable />
        </AssistantGenerateRoutesDialogCollapsableCard>
      </Stack>

      <Stack>
        <AssistantGenerateRoutesDialogCollapsableCard
          expanded={expandedVehiclesCard}
          onExpanded={() => toogleExpandedVehiclesCard()}
          title="VEHÍCULOS A PROGRAMAR"
          description={getSelectedEntityDescription(
            selectedVehiclesIds,
            'vehículo',
          )}
          indicator={
            <AssistantGenerateRoutesDialogCapabilitiesInfo
              capacity1={indicatorsVehiclesCharge.capacity1}
              capacity2={indicatorsVehiclesCharge.capacity2}
              maximumVisits={null}
              fontSize={14}
              fontWeight="bold"
              autoFlow="row"
              rows={1}
              columns={2}
            />
          }
        >
          <AssistantGenerateRoutesDialogByItemsContentFormVehiclesTable
            loading={vehiclesTable.loading}
            onChange={vehiclesTable.handleChangeTable}
            pagination={vehiclesTable.queryParams.pagination}
            totalSize={vehiclesTable.totalSize}
          />
        </AssistantGenerateRoutesDialogCollapsableCard>
      </Stack>

      {isPlanStart(subscription) && (
        <Alert
          severity="info"
          variant="standard"
          icon={false}
          sx={(currentTheme) => ({
            border: `1px solid ${currentTheme.palette.info.light}`,
          })}
        >
          ¿Necesitas programar varios vehículos a la vez?.{' '}
          <Link
            color="primary"
            underline="hover"
            fontWeight="bold"
            component={RouterLink}
            to={getStringifyUrlQueryString({
              url: PATHS.CONFIG.SUBSCRIPTIONS.BASE,
              query: {
                currentTab: 'plans-packages',
              },
            })}
            onClick={onClose}
          >
            ¡Prueba nuestros planes superiores y simplifica el ruteo de toda tu
            flota!
          </Link>
        </Alert>
      )}

      {!isScheduleRouteEnabled && (
        <>
          <Stack direction="row">
            <FormGroup row sx={{ alignItems: 'center' }}>
              <FormControlLabel
                label="Distribuir paradas entre todos los vehículos seleccionados."
                control={
                  <Switch
                    {...getFieldProps(
                      FORM_BY_ITEMS_FIELDS_NAME.LOAD_BALACING_VEHICLES,
                    )}
                    checked={
                      values[FORM_BY_ITEMS_FIELDS_NAME.LOAD_BALACING_VEHICLES]
                    }
                  />
                }
                sx={{ margin: 0 }}
              />
            </FormGroup>
          </Stack>

          <Stack direction="row">
            <FormGroup row sx={{ alignItems: 'center' }}>
              <FormControlLabel
                label="Permitir generar más de una ruta por vehículo."
                control={
                  <Switch
                    {...getFieldProps(
                      FORM_BY_ITEMS_FIELDS_NAME.MULTIPLE_ROUTES_PER_VEHICLE,
                    )}
                    checked={
                      values[
                        FORM_BY_ITEMS_FIELDS_NAME.MULTIPLE_ROUTES_PER_VEHICLE
                      ]
                    }
                  />
                }
                sx={{ margin: 0 }}
              />
            </FormGroup>
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default AssistantGenerateRoutesDialogByItemsContentForm;
